// @owners { team: patient-health }
import { ActionSheetContext, ActionSheetV2, ListBase } from '@alto/design-system';
import {
  AccountActionSheet,
  MenuOption,
  OrderingActionSheet,
  PricingPaymentsActionSheet,
  TransferActionSheet,
} from '@alto/features';
import React, { useContext, useEffect } from 'react';
import { push } from 'react-router-redux';
import { ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import {
  sendAssistantCategoryChosen,
  sendAssistantViewedEvent,
} from '~shared/features/alto-assistant/analytics/helpers';
import {
  ASSISTANT_CATEGORIES,
  ASSISTANT_CHAT_ORIGINS,
  ASSISTANT_MESSAGE_SUBJECTS,
  type AssistantCategory,
  type AssistantMessageSubject,
  MESSAGE_PROMPTS,
} from '~shared/features/alto-assistant/constants';
import { useDispatchShared } from '~shared/store';

export const HowCanWeHelpActionSheet = () => {
  const dispatch = useDispatchShared();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);

  useEffect(() => {
    dispatch(sendAssistantViewedEvent(ASSISTANT_VIEWED_EVENTS.CATEGORY_ACTION_SHEET));
  }, [dispatch]);

  const navigateToMessageForm = (category: AssistantCategory, subject?: AssistantMessageSubject) => () => {
    closeActionSheet();
    dispatch(sendAssistantCategoryChosen(category));
    dispatch(
      push({
        pathname: '/help/message',
        state: {
          category,
          origin: ASSISTANT_CHAT_ORIGINS.ALTO_ASSISTANT_CHOOSE_CATEGORY_MENU,
          messagePrompt: MESSAGE_PROMPTS.LET_US_HELP,
          subject,
        },
      }),
    );
  };

  const handlePressAccount = () => {
    dispatch(sendAssistantCategoryChosen('account'));
    setActiveActionSheet(<AccountActionSheet />);
  };

  const handlePressPricingAndPayments = () => {
    dispatch(sendAssistantCategoryChosen('pricing_payments'));
    setActiveActionSheet(<PricingPaymentsActionSheet />);
  };

  const handlePressOrdering = () => {
    dispatch(sendAssistantCategoryChosen('ordering'));
    setActiveActionSheet(<OrderingActionSheet />);
  };

  const handlePressTransferPrescriptions = () => {
    dispatch(sendAssistantCategoryChosen('transfer'));
    setActiveActionSheet(<TransferActionSheet />);
  };

  const items = [
    {
      key: 'deliveries',
      component: (
        <MenuOption
          text="Deliveries"
          iconName="box-duo"
          onPress={navigateToMessageForm(ASSISTANT_CATEGORIES.DELIVERIES, ASSISTANT_MESSAGE_SUBJECTS.DELIVERY)}
          key="deliveries"
        />
      ),
    },
    {
      key: 'ordering',
      component: (
        <MenuOption
          text="Ordering"
          iconName="truck-duo"
          onPress={handlePressOrdering}
          key="ordering"
        />
      ),
    },
    {
      key: 'account',
      component: (
        <MenuOption
          text="Account"
          iconName="usercircle-duo"
          onPress={handlePressAccount}
          key="account"
        />
      ),
    },
    {
      key: 'medications',
      component: (
        <MenuOption
          text="Medications"
          iconName="pillbottle-duo"
          onPress={navigateToMessageForm(ASSISTANT_CATEGORIES.MEDICATIONS, ASSISTANT_MESSAGE_SUBJECTS.MEDICATION)}
          key="medications"
        />
      ),
    },
    {
      key: 'pricing-and-payments',
      component: (
        <MenuOption
          text="Pricing & payments"
          iconName="creditcard-duo"
          onPress={handlePressPricingAndPayments}
          key="pricing-payments"
        />
      ),
    },
    {
      key: 'transfer-prescriptions',
      component: (
        <MenuOption
          text="Transfer prescriptions"
          iconName="swap-duo"
          onPress={handlePressTransferPrescriptions}
          key="transfer-prescriptions"
        />
      ),
    },
  ];

  return (
    <ActionSheetV2
      title="How can we help?"
      analyticsName="assistant help"
    >
      <ListBase items={items} />
    </ActionSheetV2>
  );
};
